import React, { useCallback, useEffect, useState } from "react";
import {
  FileLocation,
  FileLocationExplored,
  FileLocationProviderContext,
  SelectedItemLocation,
} from "./FileLocationProvider";
import { PageDocumentRoots } from "../../api/models/page-document-roots";

interface InternalState {
  applicationCurrentLocation: FileLocation;
  exploredLocation: FileLocationExplored;
  selectedItemLocation: SelectedItemLocation;
  isLoading: boolean;
  searchQuery: string;
}

export interface WithApiFileLocationProviderProps {
  cleanState?: boolean;
  selectedItem?: SelectedItemLocation;
  getItems: (path?: string) => Promise<FileLocation[]>;
  selectDepth: "FOLDER" | "ITEM";
  pagination?: PageDocumentRoots;
}

export const DEFAULT_FOLDER_CREATION = "Shared Documents";

export const WithApiFileLocationProvider: React.FunctionComponent<WithApiFileLocationProviderProps> = (props) => {
  const [state, setState] = useState<InternalState>();
  const { pagination, cleanState, selectedItem, getItems } = props;

  useEffect(() => {
    if (cleanState) {
      setState((s) => ({ ...s, isLoading: true }));
      getItems(null).then((loc) => {
        let location = {
          key: DEFAULT_FOLDER_CREATION,
          text: DEFAULT_FOLDER_CREATION,
          document: { folder: true, docName: DEFAULT_FOLDER_CREATION, docDescription: "" },
        };
        setState((s) => ({
          ...s,
          applicationCurrentLocation: location,
          selectedItemLocation: null,
          exploredLocation: {
            item: location,
            path: [],
            children: loc,
            page: pagination,
          },
          isLoading: false,
        }));
      });
    }
  }, [cleanState]);

  useEffect(() => {
    if (pagination && pagination.totalElements > 0) {
      setState((s) => ({
        ...s,
        exploredLocation: { ...s.exploredLocation, page: pagination },
      }));
    }
  }, [pagination]);

  // eslint-disable-next-line no-undef
  console.log("rendering WithApiFileLocationProvider with state", state);

  const navigateRoot = () => {
    // eslint-disable-next-line no-undef
    console.log("navigateRoot");

    getItems(null).then((loc) => {
      let location = {
        key: DEFAULT_FOLDER_CREATION,
        text: DEFAULT_FOLDER_CREATION,
        document: { folder: true, docName: DEFAULT_FOLDER_CREATION, docDescription: "" },
      };
      setState((s) => ({
        ...s,
        applicationCurrentLocation: location,
        selectedItemLocation: null,
        exploredLocation: {
          item: location,
          path: [],
          children: loc,
          page: s?.exploredLocation?.page,
        },
      }));
    });
  };

  useEffect(() => {
    if (selectedItem) {
      setState((s) => ({
        ...s,
        applicationCurrentLocation: selectedItem.fileLocationExplored.item,
        selectedItemLocation: null,
        exploredLocation: selectedItem.fileLocationExplored,
        isLoading: false,
      }));
    } else {
      navigateRoot();
    }
  }, []);

  const exploreLocation = useCallback(
    (fileLocation: FileLocation) => {
      setState((prev) => ({ ...prev, isLoading: true }));
      getItems(fileLocation.path).then((loc) => {
        setState((prev) => {
          const currentPath = prev?.exploredLocation?.path || [];
          const fileLocationIndexInPath = currentPath.findIndex((item) => item.key === fileLocation.key);
          let newPath = [...currentPath, prev.exploredLocation.item];
          if (fileLocationIndexInPath > -1) {
            newPath = currentPath.slice(0, fileLocationIndexInPath);
          }
          return {
            ...prev,
            exploredLocation: {
              item: fileLocation,
              children: loc,
              path: newPath,
              page: prev?.exploredLocation?.page,
            },
            selectedItemLocation: props.selectDepth === "FOLDER" ? prev.selectedItemLocation : null,
            isLoading: false,
          };
        });
      });
    },
    [getItems, props.selectDepth] // no longer depending on state directly
  );

  const selectApplicationCurrentLocation = useCallback(
    (fileLocation: FileLocation) => {
      setState((s) => ({
        ...s,
        applicationCurrentLocation: fileLocation,
      }));
    },
    [state]
  );

  const selectItemLocation = useCallback(
    (fileLocation: FileLocation) => {
      let path = "";
      if (fileLocation != null) {
        if (state.exploredLocation.path != null && state.exploredLocation.path.length > 0) {
          state.exploredLocation.path.forEach((p) => {
            path = path + p.text + ">";
          });
        }
        path = path + state.exploredLocation.item.text + ">";
        path = path + fileLocation.text;
        fileLocation.path = path;
      }
      let selectedItemLocation: SelectedItemLocation = {
        fileLocation: fileLocation,
        fileLocationPath: path,
        fileLocationExplored: state.exploredLocation,
      };
      setState((s) => ({
        ...s,
        selectedItemLocation: selectedItemLocation,
      }));
    },
    [state]
  );

  function updateChildren(loc, page) {
    const exploredLocation = state.exploredLocation;
    setState({
      ...state,
      exploredLocation: {
        item: exploredLocation.item,
        path: exploredLocation.path,
        children: loc,
        page: page,
      },
    });
  }

  return state && !state.isLoading ? (
    <FileLocationProviderContext.Provider
      value={{
        exploredLocation: state.exploredLocation,
        applicationCurrentLocation: state.applicationCurrentLocation,
        exploreLocation: exploreLocation,
        selectApplicationCurrentLocation: selectApplicationCurrentLocation,
        selectItemLocation: selectItemLocation,
        selectedItemLocation: state.selectedItemLocation,
        updateChildren: updateChildren,
      }}
    >
      {props.children}
    </FileLocationProviderContext.Provider>
  ) : null;
};
